// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTierTheme } from './useTierTheme';
import ErrorBoundary from './components/ErrorBoundary';
import LegalWatermark from './components/LegalWatermark';
import LicenseGate from './components/LicenseGate';

// Layout
const Layout = lazy(() => import('./components/Layout'));

// Auth pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Onboarding
const PasswordChangePage = lazy(() => import('./pages/PasswordChangePage'));
const NDASignaturePage = lazy(() => import('./pages/NDASignaturePage'));
const AppTutorialPage = lazy(() => import('./pages/AppTutorialPage'));

// Core
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const OverwatchPage = lazy(() => import('./pages/OverwatchPage'));

// Events
const EventSetupPage = lazy(() => import('./pages/EventSetupPage'));
const EventCalendarPage = lazy(() => import('./pages/EventCalendarPage'));
const TicketingPage = lazy(() => import('./pages/TicketingPage'));
const SeasonalEventsPage = lazy(() => import('./pages/SeasonalEventsPage'));

// Venues
const VenueManagementPage = lazy(() => import('./pages/VenueManagementPage'));
const CampingPage = lazy(() => import('./pages/CampingPage'));
const VendorZonesPage = lazy(() => import('./pages/VendorZonesPage'));

// Entertainment
const PerformersPage = lazy(() => import('./pages/PerformersPage'));
const AttractionsPage = lazy(() => import('./pages/AttractionsPage'));
const ShowSchedulesPage = lazy(() => import('./pages/ShowSchedulesPage'));

// Safety & Compliance
const CrowdSafetyPage = lazy(() => import('./pages/CrowdSafetyPage'));
const PermitsPage = lazy(() => import('./pages/PermitsPage'));
const WeatherPage = lazy(() => import('./pages/WeatherPage'));

// Staff
const StaffPage = lazy(() => import('./pages/StaffPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const ShiftAssignmentsPage = lazy(() => import('./pages/ShiftAssignmentsPage'));

// Financial
const BoxOfficePage = lazy(() => import('./pages/BoxOfficePage'));
const SponsorshipsPage = lazy(() => import('./pages/SponsorshipsPage'));
const BudgetsPage = lazy(() => import('./pages/BudgetsPage'));

// Recording & Reports
const RecordingsPage = lazy(() => import('./pages/RecordingsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));

// Account
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

const Loading = () => (
  <div className="loading-screen">
    <div className="loading-spinner" />
    <p style={{ marginTop: 16, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading, onboardingStep } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (onboardingStep === 'password') return <Navigate to="/change-password" replace />;
  if (onboardingStep === 'nda') return <Navigate to="/sign-nda" replace />;
  if (onboardingStep === 'tutorial') return <Navigate to="/tutorial" replace />;
  return children;
}

function OnboardingRoute({ step, children }) {
  const { user, loading, onboardingStep } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (onboardingStep !== step) return <Navigate to="/dashboard" replace />;
  return children;
}

const ADMIN_TEAM = [
  'rmiskulin@gmail.com',
  'J.lutz@miskulintechnologies.com',
  'al@redneckcountryclub.com',
  'threewayindustries@gmail.com'
];

const OVERWATCH_TEAM = [
  'rmiskulin@gmail.com',
  'J.lutz@miskulintechnologies.com'
];

function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  const isOwner = user.role === 'owner' || OVERWATCH_TEAM.includes(user.email?.toLowerCase());
  if (!isOwner) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  const { user } = useAuth();
  useTierTheme(user);

  return (
    <ErrorBoundary>
      <LicenseGate>
        <LegalWatermark />
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />

            {/* Onboarding */}
            <Route path="/change-password" element={<OnboardingRoute step="password"><PasswordChangePage /></OnboardingRoute>} />
            <Route path="/sign-nda" element={<OnboardingRoute step="nda"><NDASignaturePage /></OnboardingRoute>} />
            <Route path="/tutorial" element={<OnboardingRoute step="tutorial"><AppTutorialPage /></OnboardingRoute>} />

            {/* Protected with Layout */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/overwatch" element={<OwnerRoute><OverwatchPage /></OwnerRoute>} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/events" element={<EventSetupPage />} />
              <Route path="/event-calendar" element={<EventCalendarPage />} />
              <Route path="/ticketing" element={<TicketingPage />} />
              <Route path="/seasonal-events" element={<SeasonalEventsPage />} />
              <Route path="/venues" element={<VenueManagementPage />} />
              <Route path="/camping" element={<CampingPage />} />
              <Route path="/vendor-zones" element={<VendorZonesPage />} />
              <Route path="/performers" element={<PerformersPage />} />
              <Route path="/attractions" element={<AttractionsPage />} />
              <Route path="/show-schedules" element={<ShowSchedulesPage />} />
              <Route path="/crowd-safety" element={<CrowdSafetyPage />} />
              <Route path="/permits" element={<PermitsPage />} />
              <Route path="/weather" element={<WeatherPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/shift-assignments" element={<ShiftAssignmentsPage />} />
              <Route path="/box-office" element={<BoxOfficePage />} />
              <Route path="/sponsorships" element={<SponsorshipsPage />} />
              <Route path="/budgets" element={<BudgetsPage />} />
              <Route path="/recordings" element={<RecordingsPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/attendance" element={<AttendancePage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* Demo routes (no auth) */}
            <Route element={<Layout isDemo />}>
              <Route path="/demo/dashboard" element={<DashboardPage />} />
              <Route path="/demo/overwatch" element={<OverwatchPage />} />
              <Route path="/demo/events" element={<EventSetupPage />} />
              <Route path="/demo/event-calendar" element={<EventCalendarPage />} />
              <Route path="/demo/ticketing" element={<TicketingPage />} />
              <Route path="/demo/seasonal-events" element={<SeasonalEventsPage />} />
              <Route path="/demo/venues" element={<VenueManagementPage />} />
              <Route path="/demo/camping" element={<CampingPage />} />
              <Route path="/demo/vendor-zones" element={<VendorZonesPage />} />
              <Route path="/demo/performers" element={<PerformersPage />} />
              <Route path="/demo/attractions" element={<AttractionsPage />} />
              <Route path="/demo/show-schedules" element={<ShowSchedulesPage />} />
              <Route path="/demo/crowd-safety" element={<CrowdSafetyPage />} />
              <Route path="/demo/permits" element={<PermitsPage />} />
              <Route path="/demo/weather" element={<WeatherPage />} />
              <Route path="/demo/staff" element={<StaffPage />} />
              <Route path="/demo/security" element={<SecurityPage />} />
              <Route path="/demo/shift-assignments" element={<ShiftAssignmentsPage />} />
              <Route path="/demo/box-office" element={<BoxOfficePage />} />
              <Route path="/demo/sponsorships" element={<SponsorshipsPage />} />
              <Route path="/demo/budgets" element={<BudgetsPage />} />
              <Route path="/demo/recordings" element={<RecordingsPage />} />
              <Route path="/demo/analytics" element={<AnalyticsPage />} />
              <Route path="/demo/attendance" element={<AttendancePage />} />
              <Route path="/demo/profile" element={<ProfilePage />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </LicenseGate>
    </ErrorBoundary>
  );
}
